import { z } from "zod";
import { changeActorPath } from "./change-log";
import { calendarDay } from "./profile";
/**
 * One point of a body-measurement series on the wire — `POST /me/measurements`
 * (`D68`, `D69`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── WHY THIS ROUTE EXISTS, WRITTEN 2026-09-01 ──────────────────────────────
 * It did not, and `weight` is the fourth question of Session 1.
 *
 * Until `D126` the intake ran inside the chat, and the ONE answer with no
 * route of its own was the weight: the app sent the number as a message and
 * the agent recorded it through its `recordMeasurement` tool
 * (`answer.ts:63-70`, deliberate at the time). `D126` takes the model off the
 * data path entirely — the app compiles every answer and writes it, as the
 * person — so that arrangement is no longer available, and the only writer in
 * the system was `AgentEntrypoint.recordMeasurement`, which is RPC-only and
 * stamps `actorPath: "agent"`.
 *
 * That gap is not cosmetic. `progressFrom` adds `weight` to `completed` only
 * when `hasWeight` (`script.ts`), so an intake that cannot write a weight is
 * an intake `GET /me/onboarding` never reports as `done` — and an entry point
 * that gates on `done` sends a person who has just answered twenty questions
 * back to the first one.
 *
 * ── IT APPENDS, WHICH IS THE ONE NON-IDEMPOTENT WRITE OF THE INTAKE ────────
 * Every other Session 1 route replaces wholly: the same `PUT` sent twice
 * leaves the same row. This one appends — two weigh-ins on two days are two
 * rows, and both survive, because the change IS the data (`record-measurement.ts`).
 * So a client retrying after a failure it could not classify must decide
 * whether to send it again, and `apps/mobile/src/lib/onboarding/commit.ts`
 * records that decision at the site where it is made.
 *
 * ── `POST`, NOT `PUT` ──────────────────────────────────────────────────────
 * For the same reason. A `PUT` on a collection promises replacement, and
 * nothing here replaces; the series is the point.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/**
 * `weight`, `body_fat`, `circumference:<site>` — open-ended (`D68`).
 *
 * Not an enum: the series is deliberately open, so a kind the server has never
 * seen is stored rather than refused.
 *
 * ── THE TWO LENGTHS ARE THE AGGREGATE'S, AND NOT A CHARACTER TIGHTER ───────
 * `BodyMeasurement`'s `MAX_KIND_LENGTH` (64) and `MAX_UNIT_LENGTH` (16) are
 * restated here because `core/model` may not import the contract and the
 * contract may not import the module. A bound LOOSER than the aggregate's
 * turns a too-long kind into a 500 from `ValidationError` instead of a 400 a
 * screen can render; a bound tighter refuses a value the database would have
 * taken. Equal is the only right answer, and unlike `MAX_MOTIVATION_LENGTH`
 * this pair has no test holding the two copies equal — see the T11 Result.
 */
export const MAX_MEASUREMENT_KIND_LENGTH = 64;
/** `kg`, `lb`, `%`, `cm`. `MAX_UNIT_LENGTH` in the aggregate; see above. */
export const MAX_MEASUREMENT_UNIT_LENGTH = 16;
/**
 * A bound on a body, not a policy — the same number the agent's own
 * `recordMeasurementRequest` carries (`agent/data-access.ts`). The aggregate
 * itself bounds only positivity and finiteness, so this ceiling exists to stop
 * a mistyped 8000 kg from entering a series nothing else would question.
 */
export const MAX_MEASUREMENT_VALUE = 1000;
/**
 * The bound on a client-chosen idempotency key (`D154`). A UUID is 36; the
 * ceiling leaves room for a prefix without letting the column hold a note.
 */
export const MAX_IDEMPOTENCY_KEY_LENGTH = 64;
const measurementShape = {
    /**
     * The answer to the retry question the docblock above leaves with the
     * client (`D154`): a key the client mints per ATTEMPT and resends on a
     * retry, unique per account, so a repeat answers the row already written
     * instead of appending a twin. Optional — a client that predates it keeps
     * the old append semantics, duplicates and all.
     */
    idempotencyKey: z.string().min(1).max(MAX_IDEMPOTENCY_KEY_LENGTH).optional(),
    kind: z.string().min(1).max(MAX_MEASUREMENT_KIND_LENGTH),
    /**
     * The day the BODY was measured, not the day of this call. Omitted means
     * now, on the SERVER's clock — stepping off the scale and typing it in,
     * which is the intake's case, and the case where a phone with a wrong clock
     * must not decide the row's date.
     *
     * A DAY and not an instant, for `calendarDay`'s own reason: an instant
     * acquires a timezone that something then has to guess.
     */
    measuredAt: calendarDay.optional(),
    unit: z.string().min(1).max(MAX_MEASUREMENT_UNIT_LENGTH),
    value: z.number().positive().max(MAX_MEASUREMENT_VALUE),
};
export const measurementInput = z.strictObject(measurementShape);
/**
 * What was appended. `z.object` and not `z.strictObject`, like every other
 * output here: a response the server later widens must not fail the parse in
 * an app already installed.
 *
 * The id comes back because a correction is *a new row plus a delete of the
 * wrong one* (`record-measurement.ts`) — a client that could not name the row
 * it just wrote could not correct it.
 */
export const measurementCreated = z.object({
    kind: z.string(),
    /** The stored instant, ISO. */
    measuredAt: z.iso.datetime(),
    measurementId: z.string(),
    unit: z.string(),
    value: z.number(),
});
/**
 * `GET /me/measurements?kind&from&to` — one series at a time (`D154`).
 *
 * `kind` is required: the series is the unit the weight card reads, and a
 * query for every kind at once is an export, billed per row (`T-6`). Days,
 * not instants, for `sessionListQuery`'s reason.
 */
export const measurementQuery = z.object({
    /** Inclusive. Omitted = from the first point ever recorded. */
    from: calendarDay.optional(),
    kind: z.string().min(1).max(MAX_MEASUREMENT_KIND_LENGTH),
    /** Inclusive. Omitted = up to the most recent. */
    to: calendarDay.optional(),
});
/**
 * One point of the series. `measurementCreated` plus the actor pair's path
 * (`D69`): a weight the assistant recorded on the person's behalf is a
 * different fact from one they typed, and the list may say so.
 */
export const measurementRow = z.object({
    actorPath: changeActorPath,
    kind: z.string(),
    measuredAt: z.iso.datetime(),
    measurementId: z.string(),
    unit: z.string(),
    value: z.number(),
});
export const measurementListOutput = z.object({
    kind: z.string(),
    /** The most recent point of the WHOLE series, whatever the range asked —
     *  the card's headline number. `null` for a series with no points. */
    latest: measurementRow.nullable(),
    /** Oldest first — chart order, within the range. */
    rows: z.array(measurementRow),
});
// ── Circumferences (`D207`) ─────────────────────────────────────────────────
/**
 * The tape-measure sites the "Medidas" card offers, in the order it draws
 * them — head to feet, left before right. Each is a series of its own,
 * `circumference:<site>`, in centimetres (`D68`'s open kind: the server
 * stores any well-formed kind, so a site added here needs no migration and
 * no server release — only this list, which is the APP's vocabulary).
 *
 * Left and right are separate series and never averaged: an arm that grows
 * on one side only is exactly what a person training a weak side wants to
 * see, and a mean would hide it.
 */
export const CIRCUMFERENCE_SITES = [
    "neck",
    "shoulders",
    "chest",
    "waist",
    "hips",
    "arm_left",
    "arm_right",
    "forearm_left",
    "forearm_right",
    "thigh_left",
    "thigh_right",
    "calf_left",
    "calf_right",
];
/** The family every circumference kind belongs to — `circumference:<site>`. */
export const CIRCUMFERENCE_FAMILY = "circumference";
/** Always centimetres: a tape reads in cm here, and a series never mixes. */
export const CIRCUMFERENCE_UNIT = "cm";
/** `circumference:<site>`, built in one place (the module has its twin). */
export function circumferenceKind(site) {
    return `${CIRCUMFERENCE_FAMILY}:${site}`;
}
/** The site a stored kind names, or `null` for any kind this app does not
 *  offer — a series another client wrote is shown by nobody, not misread. */
export function circumferenceSiteOf(kind) {
    const prefix = `${CIRCUMFERENCE_FAMILY}:`;
    if (!kind.startsWith(prefix)) {
        return null;
    }
    const site = kind.slice(prefix.length);
    return CIRCUMFERENCE_SITES.includes(site)
        ? site
        : null;
}
/**
 * `GET /me/measurement-summary?family=circumference` — every series of one
 * family, reduced to its newest point and the one before (`D207`).
 *
 * One request where the card would otherwise make thirteen, served by one
 * indexed range over `(account_id, kind, measured_at)` (`T-6`). `family` is
 * the kind's first segment and nothing else — the server refuses a colon.
 * Strict: a query key this server does not know is a 422, not ignored.
 */
export const measurementSummaryQuery = z.strictObject({
    family: z
        .string()
        .min(1)
        .max(32)
        .regex(/^[a-z][a-z0-9_]*$/),
});
export const measurementSummaryOutput = z.object({
    family: z.string(),
    /** One entry per kind that has a visible point, kinds in order. A kind
     *  with none is absent — the card's empty row is the app's to draw. */
    kinds: z.array(z.object({
        kind: z.string(),
        latest: measurementRow,
        /** The point before `latest`, `null` when there is only one. */
        previous: measurementRow.nullable(),
    })),
});
//# sourceMappingURL=measurement.js.map